import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Download, LogOut, Plus, Trash2, FileText, LayoutDashboard, Inbox, Users, Briefcase, FolderKanban, Settings as SettingsIcon, UserCog, Quote, Image as ImageIcon, CheckCircle2, Circle } from "lucide-react";
import { api, clearToken, getToken } from "../lib/api";
import { JOB_CATEGORIES, PROJECT_FILTERS } from "../data/content";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "enquiries", label: "Enquiries", icon: Inbox },
  { id: "applications", label: "Applications", icon: Users },
  { id: "jobs", label: "Jobs", icon: Briefcase },
  { id: "projects", label: "Projects", icon: FolderKanban },
  { id: "leadership", label: "Leadership", icon: UserCog },
  { id: "testimonials", label: "Testimonials", icon: Quote },
  { id: "clients", label: "Client Logos", icon: ImageIcon },
  { id: "settings", label: "Settings", icon: SettingsIcon },
];

const download = async (path, filename) => {
  try {
    const res = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  } catch { toast.error("Download failed."); }
};

const Admin = () => {
  const nav = useNavigate();
  const [ready, setReady] = useState(false);
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState({});
  const [enquiries, setEnquiries] = useState([]);
  const [apps, setApps] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [leadership, setLeadership] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [clients, setClients] = useState([]);
  const [settings, setSettings] = useState(null);

  const logout = useCallback(() => { clearToken(); nav("/login"); }, [nav]);

  useEffect(() => {
    if (!getToken()) { nav("/login"); return; }
    api.get("/auth/me").then(() => setReady(true)).catch(() => { clearToken(); nav("/login"); });
  }, [nav]);

  const loadAll = useCallback(() => {
    const arr = (d) => (Array.isArray(d) ? d : []);
    api.get("/stats").then(({ data }) => setStats(data && typeof data === "object" ? data : {})).catch(() => {});
    api.get("/enquiries").then(({ data }) => setEnquiries(arr(data))).catch(() => {});
    api.get("/applications").then(({ data }) => setApps(arr(data))).catch(() => {});
    api.get("/jobs?all=true").then(({ data }) => setJobs(arr(data))).catch(() => {});
    api.get("/projects").then(({ data }) => setProjects(arr(data))).catch(() => {});
    api.get("/leadership?all=true").then(({ data }) => setLeadership(arr(data))).catch(() => {});
    api.get("/testimonials?all=true").then(({ data }) => setTestimonials(arr(data))).catch(() => {});
    api.get("/clients?all=true").then(({ data }) => setClients(arr(data))).catch(() => {});
    api.get("/settings").then(({ data }) => setSettings(data && typeof data === "object" ? data : null)).catch(() => {});
  }, []);

  useEffect(() => { if (ready) loadAll(); }, [ready, loadAll]);

  if (!ready) return <div className="min-h-screen grid place-items-center text-[#64748B]">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#F4F6F8] flex">
      <aside className="w-60 bg-[#0B1F3A] text-white flex flex-col shrink-0 sticky top-0 h-screen">
        <div className="p-6 border-b border-white/10">
          <p className="font-head font-extrabold tracking-tight">AIRANET</p>
          <p className="text-[10px] tracking-[0.24em] text-[#F28C28]">CMS DASHBOARD</p>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-md text-sm font-medium ${tab === t.id ? "bg-[#1261A0] text-white" : "text-[#94A3B8] hover:text-white hover:bg-white/5"}`}>
              <t.icon className="h-4 w-4" />{t.label}
            </button>
          ))}
        </nav>
        <button onClick={logout} data-testid="logout-btn" className="m-3 flex items-center gap-2 px-4 py-2.5 rounded-md text-sm text-[#94A3B8] hover:text-white hover:bg-white/5"><LogOut className="h-4 w-4" />Logout</button>
      </aside>

      <main className="flex-1 p-8 overflow-x-hidden">
        {tab === "overview" && (
          <div>
            <h1 className="font-head font-bold text-2xl text-[#0B1F3A]">Overview</h1>
            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[["Enquiries", stats.enquiries], ["Applications", stats.applications], ["Active Jobs", stats.jobs], ["Projects", stats.projects]].map(([l, v]) => (
                <div key={l} className="bg-white border border-[#E2E8F0] rounded-lg p-6">
                  <p className="text-sm text-[#64748B]">{l}</p>
                  <p className="mt-2 font-head font-extrabold text-4xl text-[#1261A0]">{v ?? 0}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 bg-white border border-[#E2E8F0] rounded-lg p-6">
              <h2 className="font-head font-semibold text-[#0B1F3A]">Admin Checklist — content to confirm before publishing</h2>
              <ul className="mt-4 grid sm:grid-cols-2 gap-2 text-sm text-[#64748B] list-disc pl-5">
                {["Incorporation year", "CIN & GST", "Leadership details", "Service locations", "Employee & engineer counts", "Completed projects & outcomes", "OEM/client experience", "Certifications & proof", "Safety policy", "Approved project photos", "Approved client logos", "Testimonials", "Social media links"].map((c) => <li key={c}>{c}</li>)}
              </ul>
            </div>
          </div>
        )}

        {tab === "enquiries" && (
          <Section title="Enquiries" onExport={() => download("/enquiries/export", "enquiries.csv")}>
            <Table cols={["Date", "Name", "Company", "Email", "Service", "Location"]}
              rows={enquiries.map((e) => [fmt(e.created_at), e.full_name, e.company, e.email, e.service_required, e.project_location])} empty="No enquiries yet." />
          </Section>
        )}

        {tab === "applications" && (
          <Section title="Career Applications" onExport={() => download("/applications/export", "applications.csv")}>
            <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F4F6F8] text-left text-[#64748B]"><tr>{["Date", "Name", "Role", "Email", "Exp", "CV"].map((h) => <th key={h} className="px-4 py-3 font-medium">{h}</th>)}</tr></thead>
                <tbody>
                  {apps.length ? apps.map((a) => (
                    <tr key={a.id} className="border-t border-[#E2E8F0]">
                      <td className="px-4 py-3">{fmt(a.created_at)}</td><td className="px-4 py-3">{a.full_name}</td><td className="px-4 py-3">{a.role}</td>
                      <td className="px-4 py-3">{a.email}</td><td className="px-4 py-3">{a.experience}</td>
                      <td className="px-4 py-3"><button onClick={() => download(`/applications/${a.id}/cv`, a.cv_filename || "cv")} data-testid={`cv-${a.id}`} className="inline-flex items-center gap-1 text-[#1261A0] hover:underline"><FileText className="h-4 w-4" />CV</button></td>
                    </tr>
                  )) : <tr><td colSpan={6} className="px-4 py-8 text-center text-[#64748B]">No applications yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </Section>
        )}

        {tab === "jobs" && <JobsPanel jobs={jobs} reload={loadAll} />}
        {tab === "projects" && <ProjectsPanel projects={projects} reload={loadAll} />}
        {tab === "leadership" && <LeadershipPanel items={leadership} reload={loadAll} />}
        {tab === "testimonials" && <TestimonialsPanel items={testimonials} reload={loadAll} />}
        {tab === "clients" && <ClientsPanel items={clients} reload={loadAll} />}
        {tab === "settings" && settings && <SettingsPanel settings={settings} setSettings={setSettings} />}
      </main>
    </div>
  );
};

const fmt = (s) => (s ? new Date(s).toLocaleDateString() : "");

const Section = ({ title, onExport, children }) => (
  <div>
    <div className="flex items-center justify-between">
      <h1 className="font-head font-bold text-2xl text-[#0B1F3A]">{title}</h1>
      {onExport && <button onClick={onExport} data-testid="export-csv" className="inline-flex items-center gap-2 bg-[#0B1F3A] text-white text-sm px-4 py-2.5 rounded-md hover:bg-[#1261A0]"><Download className="h-4 w-4" />Export CSV</button>}
    </div>
    <div className="mt-6">{children}</div>
  </div>
);

const Table = ({ cols, rows, empty }) => (
  <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-x-auto">
    <table className="w-full text-sm">
      <thead className="bg-[#F4F6F8] text-left text-[#64748B]"><tr>{cols.map((c) => <th key={c} className="px-4 py-3 font-medium">{c}</th>)}</tr></thead>
      <tbody>
        {rows.length ? rows.map((r, i) => <tr key={i} className="border-t border-[#E2E8F0]">{r.map((c, j) => <td key={j} className="px-4 py-3 text-[#252B33]">{c}</td>)}</tr>)
          : <tr><td colSpan={cols.length} className="px-4 py-8 text-center text-[#64748B]">{empty}</td></tr>}
      </tbody>
    </table>
  </div>
);

const JobsPanel = ({ jobs, reload }) => {
  const blank = { title: "", category: JOB_CATEGORIES[0], location: "", type: "Full-time", experience: "", description: "", active: true };
  const [f, setF] = useState(blank);
  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/jobs", f); toast.success("Job added"); setF(blank); reload(); }
    catch { toast.error("Failed to add job"); }
  };
  const del = async (id) => { await api.delete(`/jobs/${id}`); toast.success("Deleted"); reload(); };
  const cls = "border border-[#E2E8F0] rounded-md px-3 py-2 text-sm w-full";
  return (
    <Section title="Manage Jobs">
      <form onSubmit={add} className="bg-white border border-[#E2E8F0] rounded-lg p-5 grid sm:grid-cols-3 gap-3 mb-6" data-testid="job-form">
        <input required placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={cls} data-testid="job-title" />
        <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className={cls}>{JOB_CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select>
        <input required placeholder="Location" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} className={cls} data-testid="job-location" />
        <input placeholder="Experience e.g. 3-5 yrs" value={f.experience} onChange={(e) => setF({ ...f, experience: e.target.value })} className={cls} />
        <input placeholder="Type" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} className={cls} />
        <input placeholder="Short description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className={cls} />
        <button data-testid="job-add" className="sm:col-span-3 inline-flex justify-center items-center gap-2 bg-[#F28C28] text-[#0B1F3A] font-semibold py-2.5 rounded-md"><Plus className="h-4 w-4" />Add Job</button>
      </form>
      <div className="grid md:grid-cols-2 gap-4">
        {jobs.map((j) => (
          <div key={j.id} className="bg-white border border-[#E2E8F0] rounded-lg p-5 flex items-start justify-between">
            <div><p className="font-head font-semibold text-[#0B1F3A]">{j.title}</p><p className="text-sm text-[#64748B]">{j.category} · {j.location} · {j.type}</p></div>
            <button onClick={() => del(j.id)} data-testid={`job-del-${j.id}`} className="text-[#64748B] hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        {!jobs.length && <p className="text-[#64748B]">No jobs posted.</p>}
      </div>
    </Section>
  );
};

const ProjectsPanel = ({ projects, reload }) => {
  const blank = { title: "", category: PROJECT_FILTERS[0], client_type: "", location: "", period: "", requirement: "", challenge: "", scope: "", approach: "", result: "", testimonial: "Client name withheld for commercial confidentiality.", image: "" };
  const [f, setF] = useState(blank);
  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/projects", f); toast.success("Project added"); setF(blank); reload(); }
    catch { toast.error("Failed to add project"); }
  };
  const del = async (id) => { await api.delete(`/projects/${id}`); toast.success("Deleted"); reload(); };
  const cls = "border border-[#E2E8F0] rounded-md px-3 py-2 text-sm w-full";
  return (
    <Section title="Manage Projects">
      <form onSubmit={add} className="bg-white border border-[#E2E8F0] rounded-lg p-5 grid sm:grid-cols-2 gap-3 mb-6" data-testid="project-form">
        <input required placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={cls} data-testid="project-title" />
        <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className={cls}>{PROJECT_FILTERS.map((c) => <option key={c}>{c}</option>)}</select>
        <input placeholder="Client type" value={f.client_type} onChange={(e) => setF({ ...f, client_type: e.target.value })} className={cls} />
        <input placeholder="Location" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} className={cls} />
        <input placeholder="Period" value={f.period} onChange={(e) => setF({ ...f, period: e.target.value })} className={cls} />
        <input placeholder="Image URL" value={f.image} onChange={(e) => setF({ ...f, image: e.target.value })} className={cls} />
        <textarea placeholder="Requirement" value={f.requirement} onChange={(e) => setF({ ...f, requirement: e.target.value })} className={`${cls} sm:col-span-2`} />
        <textarea placeholder="Scope delivered" value={f.scope} onChange={(e) => setF({ ...f, scope: e.target.value })} className={`${cls} sm:col-span-2`} />
        <button data-testid="project-add" className="sm:col-span-2 inline-flex justify-center items-center gap-2 bg-[#F28C28] text-[#0B1F3A] font-semibold py-2.5 rounded-md"><Plus className="h-4 w-4" />Add Project</button>
      </form>
      <div className="grid md:grid-cols-2 gap-4">
        {projects.map((p) => (
          <div key={p.id} className="bg-white border border-[#E2E8F0] rounded-lg p-5 flex items-start justify-between">
            <div><p className="font-head font-semibold text-[#0B1F3A]">{p.title}</p><p className="text-sm text-[#64748B]">{p.category} · {p.location}</p></div>
            <button onClick={() => del(p.id)} data-testid={`project-del-${p.id}`} className="text-[#64748B] hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        {!projects.length && <p className="text-[#64748B]">No projects added.</p>}
      </div>
    </Section>
  );
};

const SettingsPanel = ({ settings, setSettings }) => {
  const [f, setF] = useState(settings);
  const save = async () => {
    try { const { data } = await api.put("/settings", f); setSettings(data); setF(data); toast.success("Settings saved"); }
    catch { toast.error("Save failed"); }
  };
  const cls = "border border-[#E2E8F0] rounded-md px-3 py-2 text-sm w-full mt-1";
  const setM = (k, v) => setF({ ...f, metrics: { ...f.metrics, [k]: v } });
  return (
    <Section title="Site Settings">
      <div className="bg-white border border-[#E2E8F0] rounded-lg p-6 grid sm:grid-cols-2 gap-4 max-w-3xl">
        {[["phone", "Phone"], ["email", "Email"], ["business_email", "Business Email"], ["careers_email", "Careers Email"], ["registration_number", "Registration No"], ["office_hours", "Office Hours"], ["whatsapp", "WhatsApp Number"], ["linkedin", "LinkedIn URL"]].map(([k, l]) => (
          <label key={k} className="block text-sm"><span className="font-medium text-[#252B33]">{l}</span><input value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={cls} /></label>
        ))}
        <label className="block text-sm sm:col-span-2"><span className="font-medium">Registered Office</span><input value={f.registered_office || ""} onChange={(e) => setF({ ...f, registered_office: e.target.value })} className={cls} /></label>
        <div className="sm:col-span-2">
          <p className="text-sm font-semibold text-[#0B1F3A] mt-2">Capability Metrics (placeholders)</p>
          <div className="mt-2 grid grid-cols-2 sm:grid-cols-5 gap-3">
            {["projects", "sites", "professionals", "states", "experience"].map((k) => (
              <label key={k} className="block text-xs"><span className="text-[#64748B] capitalize">{k}</span><input value={f.metrics?.[k] || ""} onChange={(e) => setM(k, e.target.value)} className={cls} /></label>
            ))}
          </div>
        </div>
        <button onClick={save} data-testid="settings-save" className="sm:col-span-2 bg-[#0B1F3A] text-white font-semibold py-3 rounded-md hover:bg-[#1261A0]">Save Settings</button>
      </div>
    </Section>
  );
};

const ApproveBtn = ({ item, path, reload }) => {
  const toggle = async () => {
    try { await api.put(`${path}/${item.id}`, { approved: !item.approved }); toast.success(item.approved ? "Unpublished" : "Published"); reload(); }
    catch { toast.error("Update failed"); }
  };
  return (
    <button onClick={toggle} data-testid={`approve-${item.id}`}
      className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium ${item.approved ? "bg-green-100 text-green-700" : "bg-[#F4F6F8] text-[#64748B]"}`}>
      {item.approved ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Circle className="h-3.5 w-3.5" />}
      {item.approved ? "Published" : "Draft"}
    </button>
  );
};

const cls = "border border-[#E2E8F0] rounded-md px-3 py-2 text-sm w-full";

const LeadershipPanel = ({ items, reload }) => {
  const blank = { name: "", role: "", bio: "", image: "", order: 0, approved: false };
  const [f, setF] = useState(blank);
  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/leadership", { ...f, order: Number(f.order) || 0 }); toast.success("Leader added (draft)"); setF(blank); reload(); }
    catch { toast.error("Failed to add"); }
  };
  const del = async (id) => { await api.delete(`/leadership/${id}`); toast.success("Deleted"); reload(); };
  return (
    <Section title="Leadership Profiles">
      <p className="text-sm text-[#64748B] mb-4">New profiles are saved as drafts. Publish to show them on the public About page.</p>
      <form onSubmit={add} className="bg-white border border-[#E2E8F0] rounded-lg p-5 grid sm:grid-cols-2 gap-3 mb-6" data-testid="leadership-form">
        <input required placeholder="Full name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={cls} data-testid="leadership-name" />
        <input required placeholder="Role / Title" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} className={cls} data-testid="leadership-role" />
        <input placeholder="Image URL (optional)" value={f.image} onChange={(e) => setF({ ...f, image: e.target.value })} className={cls} />
        <input type="number" placeholder="Display order" value={f.order} onChange={(e) => setF({ ...f, order: e.target.value })} className={cls} />
        <textarea placeholder="Short bio" value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })} className={`${cls} sm:col-span-2`} />
        <label className="flex items-center gap-2 text-sm sm:col-span-2"><input type="checkbox" checked={f.approved} onChange={(e) => setF({ ...f, approved: e.target.checked })} /> Publish immediately</label>
        <button data-testid="leadership-add" className="sm:col-span-2 inline-flex justify-center items-center gap-2 bg-[#F28C28] text-[#0B1F3A] font-semibold py-2.5 rounded-md"><Plus className="h-4 w-4" />Add Leader</button>
      </form>
      <div className="grid md:grid-cols-2 gap-4">
        {items.map((l) => (
          <div key={l.id} className="bg-white border border-[#E2E8F0] rounded-lg p-5 flex items-start justify-between gap-3">
            <div><p className="font-head font-semibold text-[#0B1F3A]">{l.name}</p><p className="text-sm text-[#64748B]">{l.role}</p><p className="text-xs text-[#64748B] mt-1 line-clamp-2">{l.bio}</p></div>
            <div className="flex flex-col items-end gap-2 shrink-0"><ApproveBtn item={l} path="/leadership" reload={reload} /><button onClick={() => del(l.id)} data-testid={`leadership-del-${l.id}`} className="text-[#64748B] hover:text-red-600"><Trash2 className="h-4 w-4" /></button></div>
          </div>
        ))}
        {!items.length && <p className="text-[#64748B]">No leadership profiles yet.</p>}
      </div>
    </Section>
  );
};

const TestimonialsPanel = ({ items, reload }) => {
  const blank = { quote: "", author: "", company: "", approved: false };
  const [f, setF] = useState(blank);
  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/testimonials", f); toast.success("Testimonial added (draft)"); setF(blank); reload(); }
    catch { toast.error("Failed to add"); }
  };
  const del = async (id) => { await api.delete(`/testimonials/${id}`); toast.success("Deleted"); reload(); };
  return (
    <Section title="Testimonials">
      <p className="text-sm text-[#64748B] mb-4">Publish to display approved testimonials on the public site.</p>
      <form onSubmit={add} className="bg-white border border-[#E2E8F0] rounded-lg p-5 grid gap-3 mb-6" data-testid="testimonial-form">
        <textarea required placeholder="Testimonial quote" value={f.quote} onChange={(e) => setF({ ...f, quote: e.target.value })} className={cls} data-testid="testimonial-quote" />
        <div className="grid sm:grid-cols-2 gap-3">
          <input required placeholder="Author name / role" value={f.author} onChange={(e) => setF({ ...f, author: e.target.value })} className={cls} data-testid="testimonial-author" />
          <input placeholder="Company / client type" value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} className={cls} />
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.approved} onChange={(e) => setF({ ...f, approved: e.target.checked })} /> Publish immediately</label>
        <button data-testid="testimonial-add" className="inline-flex justify-center items-center gap-2 bg-[#F28C28] text-[#0B1F3A] font-semibold py-2.5 rounded-md"><Plus className="h-4 w-4" />Add Testimonial</button>
      </form>
      <div className="grid md:grid-cols-2 gap-4">
        {items.map((t) => (
          <div key={t.id} className="bg-white border border-[#E2E8F0] rounded-lg p-5 flex items-start justify-between gap-3">
            <div><p className="text-sm text-[#252B33] italic">&ldquo;{t.quote}&rdquo;</p><p className="mt-2 text-sm font-semibold text-[#0B1F3A]">{t.author}</p><p className="text-xs text-[#64748B]">{t.company}</p></div>
            <div className="flex flex-col items-end gap-2 shrink-0"><ApproveBtn item={t} path="/testimonials" reload={reload} /><button onClick={() => del(t.id)} data-testid={`testimonial-del-${t.id}`} className="text-[#64748B] hover:text-red-600"><Trash2 className="h-4 w-4" /></button></div>
          </div>
        ))}
        {!items.length && <p className="text-[#64748B]">No testimonials yet.</p>}
      </div>
    </Section>
  );
};

const ClientsPanel = ({ items, reload }) => {
  const blank = { name: "", logo: "", approved: false };
  const [f, setF] = useState(blank);
  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/clients", f); toast.success("Client added (draft)"); setF(blank); reload(); }
    catch { toast.error("Failed to add"); }
  };
  const del = async (id) => { await api.delete(`/clients/${id}`); toast.success("Deleted"); reload(); };
  return (
    <Section title="Client Logos">
      <p className="text-sm text-[#64748B] mb-4">Add logos only with written client approval. Publish to show them on the homepage strip.</p>
      <form onSubmit={add} className="bg-white border border-[#E2E8F0] rounded-lg p-5 grid sm:grid-cols-2 gap-3 mb-6" data-testid="client-form">
        <input required placeholder="Client name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={cls} data-testid="client-name" />
        <input placeholder="Logo image URL" value={f.logo} onChange={(e) => setF({ ...f, logo: e.target.value })} className={cls} data-testid="client-logo" />
        <label className="flex items-center gap-2 text-sm sm:col-span-2"><input type="checkbox" checked={f.approved} onChange={(e) => setF({ ...f, approved: e.target.checked })} /> Publish immediately (I have written approval)</label>
        <button data-testid="client-add" className="sm:col-span-2 inline-flex justify-center items-center gap-2 bg-[#F28C28] text-[#0B1F3A] font-semibold py-2.5 rounded-md"><Plus className="h-4 w-4" />Add Client</button>
      </form>
      <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
        {items.map((c) => (
          <div key={c.id} className="bg-white border border-[#E2E8F0] rounded-lg p-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">{c.logo ? <img src={c.logo} alt={c.name} className="h-8 w-8 object-contain" /> : <ImageIcon className="h-6 w-6 text-[#64748B]" />}<p className="text-sm font-medium text-[#0B1F3A] truncate">{c.name}</p></div>
            <div className="flex flex-col items-end gap-2 shrink-0"><ApproveBtn item={c} path="/clients" reload={reload} /><button onClick={() => del(c.id)} data-testid={`client-del-${c.id}`} className="text-[#64748B] hover:text-red-600"><Trash2 className="h-4 w-4" /></button></div>
          </div>
        ))}
        {!items.length && <p className="text-[#64748B]">No client logos yet.</p>}
      </div>
    </Section>
  );
};


export default Admin;
